import React, { useState, useEffect, useCallback, useMemo, Suspense } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import MaxulusBrand from './MaxulusBrand';
import { useSession } from '../contexts/SessionContext';
import BugReportButton from './BugReportButton';
import { notificationStore, NotificationItem } from '../services/notificationStore';
import { useNotifications } from '../contexts/NotificationContext';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  useMediaQuery,
  ThemeProvider,
  GlobalStyles,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  ListItemButton,
  Badge,
  Tooltip,
  Paper,
  Chip,
  alpha,
  useTheme,
  Button,
  InputBase,
  styled,
  Stack,
  Card,
  Select,
  FormControl,
  CircularProgress
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Campaign as CampaignIcon,
  Message as MessageIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  ExitToApp as LogoutIcon,
  Menu as MenuIcon,
  Group as GroupIcon,
  WhatsApp as WhatsAppIcon,
  PhoneIphone as PhoneNumberIcon,
  Description as DescriptionIcon,
  PermMedia as MediaIcon,
  Analytics as AnalyticsIcon,
  SmartToy as SmartToyIcon,
  AutoAwesome as AutoAwesomeIcon,
  NotificationsActive as AnomalyIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  NotificationsNoneOutlined,
  Search as SearchIcon,
  KeyboardArrowDown,
  Close as CloseIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Clear as ClearIcon,
  DoneAll as DoneAllIcon,
  Notifications as NotificationsIcon,
  Assignment as AssignmentIcon,
  Forum as ForumIcon,
  Instagram as InstagramIcon,
  ChatBubbleOutline as CommentIcon,
  Link as LinkIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  MailOutline as MailOutlineIcon,
  Domain as DomainIcon,
  AlternateEmail as AlternateEmailIcon,
  Paid as PaidIcon,
  Assessment as ConversionIcon,
  ExpandLess,
  ExpandMore,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Business as BusinessIcon,
  History as HistoryIcon,
  BugReport as BugReportIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Check as CheckIcon,
  Language as LanguageIcon,
  KeyboardArrowDown as ArrowDownIcon
} from '@mui/icons-material';
import Collapse from '@mui/material/Collapse';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme as useCustomTheme } from '../contexts/ThemeContext';
import api from '../services/api';
import { formatDistanceToNow } from 'date-fns';
import { getDateFnsLocale, getLocale, localizeTheme } from '../utils/locale';
import NotificationMenu from './NotificationMenu';
import GoogleAdsDatePicker from './googleAds/GoogleAdsDatePicker';
import GoogleAdsAccountSelector from './googleAds/GoogleAdsAccountSelector';
import { ledger, EASE_OUT, COMPACT_QUERY, themeFor } from '../theme/modernTheme';
import { hasPageKey } from '../utils/pageKeys';

const COLLAPSED_WIDTH = 80;

// Styled Components
// Sidebar shares the canvas colour (one world, not a "sidebar world"); a hairline border separates it
const StyledDrawer = styled(Drawer)(({ theme }) => ({
  '& .MuiDrawer-paper': {
    border: 'none',
    borderRight: `1px solid ${ledger(theme).rule}`,
    boxShadow: 'none',
    background: ledger(theme).canvas,
  }
}));

const SearchBar = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  maxWidth: 320,
  height: 40,
  padding: '0 16px',
  borderRadius: 20,
  backgroundColor: theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.05)
    : alpha(theme.palette.common.black, 0.02),
  border: `1px solid ${theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.08)
    : alpha(theme.palette.common.black, 0.05)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark'
      ? alpha(theme.palette.common.white, 0.08)
      : alpha(theme.palette.common.black, 0.04),
    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
  },
  '&:focus-within': {
    maxWidth: 380,
    boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
    border: `1px solid ${theme.palette.primary.main}`,
  },
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  }
}));

const ModernLayout: React.FC = () => {
  const { t } = useTranslation();
  const { language, languages, canChoose, setLanguage } = useLanguage();
  // Compact screens (see COMPACT_QUERY) get a narrower sidebar and a lower header
  const compact = useMediaQuery(COMPACT_QUERY, { noSsr: true });
  const DRAWER_WIDTH = compact ? 232 : 260;
  const HEADER_HEIGHT = compact ? 54 : 60;
  const appTheme = useTheme();
  // Always wrapped (only the theme changes), so crossing the width keeps every page's state
  const appMode = appTheme.palette.mode;
  const compactTheme = useMemo(
    () => localizeTheme(themeFor(appMode === 'dark' ? 'dark' : 'light', 'compact')),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- localizeTheme() reads the language
    [appMode, language]
  );
  const densityTheme = compact ? compactTheme : appTheme;
  const navigate = useNavigate();
  const location = useLocation();
  const { sessions, activeSession, activeSessionData, setActiveSession, createNewSession, canCreateSession } = useSession();
  const isWahaPage = location.pathname.startsWith('/waha');
  const showSessionSelector = isWahaPage && sessions.length >= 1;
  const isGoogleAdsPage = location.pathname.startsWith('/google-ads');
  const isGoogleAdsDashboard = location.pathname === '/google-ads' || location.pathname.startsWith('/google-ads/dashboards');
  const theme = useTheme();
  const L = ledger(theme);
  const { user, logout } = useAuth();
  const { mode, toggleColorMode } = useCustomTheme();
  const { socket } = useNotifications();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [messageCount, setMessageCount] = useState<number>(0);
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [languageAnchor, setLanguageAnchor] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});
  const [openCategories, setOpenCategories] = useState<{ [key: string]: boolean }>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);

  const toggleMenu = (menuName: string) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const handleCategoryToggle = (category: string) => {
    setOpenCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const toggleCollapse = () => {
    setCollapsed(prev => !prev);
    // Close open menus when collapsing
    if (!collapsed) {
      setOpenMenus({});
    }
  };

  // Mentions state
  const [mentionCount, setMentionCount] = useState(0);
  const [mentionDrawerOpen, setMentionDrawerOpen] = useState(false);
  const [recentMentions, setRecentMentions] = useState<any[]>([]);
  const [mentionsLoading, setMentionsLoading] = useState(false);

  const fetchMentionCount = async () => {
    try {
      const params: any = {};
      if (activeSession) params.session = activeSession;
      const res = await api.get('/waha/mentions/unread-count', { params });
      if (res.data?.success) setMentionCount(res.data.count || 0);
    } catch {}
  };

  const fetchRecentMentions = async () => {
    setMentionsLoading(true);
    try {
      const params: any = { limit: 10 };
      if (activeSession) params.session = activeSession;
      const res = await api.get('/waha/mentions/all', { params });
      if (res.data?.success) setRecentMentions(res.data.data || []);
    } catch {} finally { setMentionsLoading(false); }
  };

  useEffect(() => {
    if (isWahaPage) fetchMentionCount();
  }, [location.pathname]);

  useEffect(() => {
    if (!socket) return;
    const handler = (data: any) => {
      if (data?.category === 'mention') fetchMentionCount();
    };
    socket.on('new_notification', handler);
    return () => { socket.off('new_notification', handler); };
  }, [socket]);

  const handleMentionDrawerOpen = () => {
    setMentionDrawerOpen(true);
    fetchRecentMentions();
  };

  const fetchMessageCount = async () => {
    if (!activeSession) return;
    try {
      const res = await api.get(`/waha/session/${activeSession}/unread-count`);
      if (res.data?.success) setMessageCount(res.data.unreadCount || 0);
    } catch { setMessageCount(0); }
  };

  // Badge for AI Asistan: questions the assistant forwarded to the team on the active number
  const [assistantQuestionCount, setAssistantQuestionCount] = useState(0);
  const mayOpenAssistant = hasPageKey((user as any)?.allowed_pages, 'waha-ai-assistant');
  const fetchAssistantQuestionCount = useCallback(async () => {
    if (!activeSession || !mayOpenAssistant) { setAssistantQuestionCount(0); return; }
    try {
      const res = await api.get(`/chatbot-assistant/${encodeURIComponent(activeSession)}/questions/open-count`);
      if (res.data?.success) setAssistantQuestionCount(res.data.data.openCount || 0);
    } catch { setAssistantQuestionCount(0); }
  }, [activeSession, mayOpenAssistant]);

  useEffect(() => { fetchAssistantQuestionCount(); }, [fetchAssistantQuestionCount]);
  useEffect(() => {
    if (!socket) return;
    // Sent when a question is forwarded, answered or dismissed
    const handler = (data: any) => { if (!data?.sessionName || data.sessionName === activeSession) fetchAssistantQuestionCount(); };
    socket.on('chatbot_question_added', handler);
    return () => { socket.off('chatbot_question_added', handler); };
  }, [socket, activeSession, fetchAssistantQuestionCount]);

  // Badge for Mesaj Talepleri — fetch on mount and session change
  useEffect(() => {
    fetchMessageCount();
  }, [activeSession]);

  useEffect(() => {
    if (!socket) return;
    const handler = (data: any) => {
      if (!data.fromMe) {
        setMessageCount(prev => prev + 1);
      }
    };
    socket.on('new_whatsapp_message', handler);
    return () => { socket.off('new_whatsapp_message', handler); };
  }, [socket]);

  // Reset badge and refetch when user visits Mesaj Talepleri page
  useEffect(() => {
    if (location.pathname === '/waha/chat') {
      setMessageCount(0);
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchMessageCount();
    const interval = setInterval(fetchMessageCount, 30000);
    
    // Listen for message status updates
    const handleMessageStatusUpdate = () => {
      fetchMessageCount();
    };
    
    // Subscribe to notification store
    const unsubscribe = notificationStore.subscribe((notifs) => {
      setNotifications(notifs);
      setNotificationCount(notificationStore.getUnreadCount());
    });
    
    window.addEventListener('messageStatusUpdated', handleMessageStatusUpdate);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('messageStatusUpdated', handleMessageStatusUpdate);
      unsubscribe();
    };
  }, []);

  // Update badge when page changes
  useEffect(() => {
    fetchMessageCount();
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleMarkAllRead = () => {
    notificationStore.markAllAsRead();
  };

  const handleClearAll = () => {
    notificationStore.clearAll();
    handleNotificationClose();
  };

  const handleNotificationItemClick = (notification: NotificationItem) => {
    notificationStore.markAsRead(notification.id);
    const data = notification.data as any;
    const category = (notification as any).category || data?.category;
    handleNotificationClose();

    // Mention / assignment / support → go to support ticket detail
    if (['mention', 'assignment', 'support'].includes(category) && data?.ticketId) {
      const msgParam = data?.messageId ? `?msg=${data.messageId}` : '';
      navigate(`/waha/support-tickets/${data.ticketId}${msgParam}`);
      return;
    }
    // Chat notification → go to chat
    if (data?.chatId) {
      navigate(`/waha/chat/${encodeURIComponent(data.chatId)}`);
      return;
    }
    if (notification.type === 'message' && data?.message) {
      navigate('/messages');
    }
  };

  interface MenuItem {
    text: string;
    icon: React.ReactElement;
    path: string;
    roles?: string[];
    color: string;
    subItems?: MenuItem[];
    condition?: (user: any) => boolean;
    /** Sub-menu group label; a label is rendered where the group changes */
    group?: string;
  }

  interface MenuCategory {
    /** Stable id for open/closed state; the title is display text */
    id: string;
    title: string;
    icon: React.ReactElement;
    color: string;
    roles: string[];
    items: MenuItem[];
  }

  // Categorized menu structure
  const menuCategories: MenuCategory[] = [
    {
      id: 'home',
      title: t('Ana Sayfa'),
      icon: <DashboardIcon />,
      color: '#5e72e4',
      roles: ['admin', 'bayi', 'personel', 'musteri'],
      items: [
        { text: t('Dashboard'), icon: <DashboardIcon />, path: '/dashboard', roles: ['admin', 'bayi', 'personel'], color: '#5e72e4' },
      ]
    },
    {
      id: 'whatsapp',
      title: 'WhatsApp',
      icon: <WhatsAppIcon />,
      color: '#1976D2',
      roles: ['admin', 'bayi', 'personel', 'musteri'],
      items: [
        { text: t('Genel Bakış'), icon: <DashboardIcon />, path: '/waha', roles: ['admin', 'bayi', 'personel', 'musteri'], color: '#1976D2' },
        { text: t('Gelen Kutusu'), icon: <MessageIcon />, path: '/waha/chat', roles: ['admin', 'bayi', 'personel', 'musteri'], color: '#1976D2' },
        { text: t('Görüşmeler'), icon: <AssignmentIcon />, path: '/waha/support-tickets', roles: ['admin', 'bayi', 'personel', 'musteri'], color: '#1976D2' },
        { text: t('Toplu Mesaj'), icon: <SendIcon />, path: '/waha/bulk', roles: ['admin', 'bayi', 'personel', 'musteri'], color: '#1976D2' },
        { text: t('Kişiler'), icon: <PersonIcon />, path: '/waha/contacts', roles: ['admin', 'bayi', 'personel', 'musteri'], color: '#1976D2' },
        { text: t('Otomasyon'), icon: <SmartToyIcon />, path: '/waha/automation', roles: ['admin', 'bayi', 'personel', 'musteri'], color: '#1976D2' },
        { text: t('AI Asistan'), icon: <AutoAwesomeIcon />, path: '/waha/ai-assistant', roles: ['admin', 'bayi', 'personel', 'musteri'], color: '#1976D2' },
        { text: t('Departmanlar'), icon: <GroupIcon />, path: '/waha/departments', roles: ['admin', 'bayi', 'personel', 'musteri'], color: '#1976D2' },
        { text: t('İş Takibi'), icon: <ForumIcon />, path: '/waha/group-tracker', roles: ['admin', 'bayi', 'personel'], color: '#1976D2' },
      ]
    },
    {
      id: 'wa-business',
      title: 'WhatsApp Business API',
      icon: <BusinessIcon />,
      color: '#1976d2',
      roles: ['admin', 'bayi', 'personel'],
      items: [
        { text: t('Numaralar'), icon: <PhoneNumberIcon />, path: '/wa-business/numbers', roles: ['admin', 'bayi', 'personel'], color: '#1976D2' },
        { text: t('Mesajlar'), icon: <ForumIcon />, path: '/messages', roles: ['admin', 'bayi', 'personel'], color: '#1976D2' },
        { text: t('AI Asistan'), icon: <AutoAwesomeIcon />, path: '/wa-business/ai-assistant', roles: ['admin', 'bayi', 'personel'], color: '#1976D2' },
        { text: t('Kişiler'), icon: <PeopleIcon />, path: '/whatsapp-contacts', roles: ['admin', 'bayi', 'personel'], color: '#1976D2' },
        { text: t('Şablonlar'), icon: <DescriptionIcon />, path: '/templates', roles: ['admin', 'bayi', 'personel'], color: '#1976D2' },
        { text: t('Analiz'), icon: <AnalyticsIcon />, path: '/analytics', roles: ['admin', 'bayi', 'personel'], color: '#1976D2' },
      ]
    },
    {
      id: 'social',
      title: t('Sosyal Medya'),
      icon: <InstagramIcon />,
      color: '#1976d2',
      roles: ['admin', 'bayi', 'personel'],
      items: [
        { text: t('Mesajlar'), icon: <ForumIcon />, path: '/social/inbox', roles: ['admin', 'bayi', 'personel'], color: '#1976D2' },
        { text: t('Yorumlar'), icon: <CommentIcon />, path: '/social/comments', roles: ['admin', 'bayi', 'personel'], color: '#1976D2' },
        { text: t('AI Asistan'), icon: <AutoAwesomeIcon />, path: '/social/ai-assistant', roles: ['admin', 'bayi', 'personel'], color: '#1976D2' },
        { text: t('Bağlantılar'), icon: <LinkIcon />, path: '/social/connections', roles: ['admin', 'bayi', 'personel'], color: '#1976D2' },
      ]
    },
    {
      id: 'marketing',
      title: t('Pazarlama'),
      icon: <CampaignIcon />,
      color: '#f5365c',
      roles: ['admin', 'bayi', 'personel'],
      items: [
        { text: t('Kampanyalar'), icon: <CampaignIcon />, path: '/campaigns', roles: ['admin', 'bayi', 'personel'], color: '#f5365c' },
        { text: t('E-posta Kampanyaları'), icon: <EmailIcon />, path: '/email-campaigns', roles: ['admin', 'bayi', 'personel'], color: '#667eea' },
        { text: t('E-posta Listeleri'), icon: <PeopleIcon />, path: '/email-lists', roles: ['admin', 'bayi', 'personel'], color: '#764ba2' },
        { text: t('SMS Kampanyaları'), icon: <SmsIcon />, path: '/sms-campaigns', roles: ['admin', 'bayi', 'personel'], color: '#17a2b8' },
        {
          text: 'Google Ads',
          icon: <PaidIcon />,
          path: '/google-ads',
          roles: ['admin', 'bayi', 'personel'],
          color: '#fbbc04',
          subItems: [
            { text: t('Dashboard'), icon: <PaidIcon />, path: '/google-ads', color: '#4285f4', group: t('Genel Bakış') },
            { text: t('Kampanyalar'), icon: <CampaignIcon />, path: '/google-ads/campaigns', color: '#fbbc04', group: t('Varlıklar') },
            { text: t('Reklam Grupları'), icon: <GroupIcon />, path: '/google-ads/ad-groups', color: '#ea4335', group: t('Varlıklar') },
            { text: t('Anahtar Kelimeler'), icon: <SearchIcon />, path: '/google-ads/keywords', color: '#4285f4', group: t('Varlıklar') },
            { text: t('Reklamlar'), icon: <DescriptionIcon />, path: '/google-ads/ads', color: '#ff6f00', group: t('Varlıklar') },
            { text: t('Arama Terimleri'), icon: <SearchIcon />, path: '/google-ads/search-terms', color: '#e91e63', group: t('Varlıklar') },
            { text: t('Dönüşümler'), icon: <ConversionIcon />, path: '/google-ads/conversions', color: '#0f9d58', group: t('Varlıklar') },
            { text: t('İçgörüler'), icon: <AnalyticsIcon />, path: '/google-ads/insights', color: '#1976D2', group: t('Analiz') },
            { text: t('Uyarılar'), icon: <AnomalyIcon />, path: '/google-ads/alerts', color: '#d32f2f', group: t('Analiz') },
            { text: t('Bütçe Takibi'), icon: <AnalyticsIcon />, path: '/google-ads/budget-pacing', color: '#f9a825', roles: ['admin', 'bayi', 'personel'], group: t('Analiz') },
            { text: t('Rapor Düzenleyici'), icon: <AnalyticsIcon />, path: '/google-ads/reports', color: '#673ab7', group: t('Analiz') },
            { text: t('AI Asistan'), icon: <SmartToyIcon />, path: '/google-ads/assistant', color: '#00897b', group: 'AI' },
            { text: t('AI Analiz'), icon: <SmartToyIcon />, path: '/google-ads/ai-analysis', color: '#7b1fa2', roles: ['admin', 'bayi', 'personel'], group: 'AI' }
          ]
        },
      ]
    },
    {
      id: 'content',
      title: t('İçerik'),
      icon: <MediaIcon />,
      color: '#8965e0',
      roles: ['admin', 'bayi', 'personel'],
      items: [
        { text: t('Medya'), icon: <MediaIcon />, path: '/media', roles: ['admin', 'bayi', 'personel'], color: '#8965e0' },
      ]
    },
    {
      id: 'crm',
      title: 'CRM',
      icon: <PeopleIcon />,
      color: '#11cdef',
      roles: ['admin', 'bayi', 'personel'],
      items: [
        { text: t('Müşteriler'), icon: <PeopleIcon />, path: '/customers', roles: ['admin', 'bayi', 'personel'], color: '#11cdef' },
        { text: t('Talepler'), icon: <AssignmentIcon />, path: '/requests', roles: ['admin', 'bayi', 'personel'], color: '#fb6340' },
        { text: t('Analiz Lead\'leri'), icon: <SmartToyIcon />, path: '/analiz-leads', roles: ['admin', 'bayi', 'personel'], color: '#5e72e4' },
      ]
    },
    {
      id: 'mail',
      title: t('Mail Yönetimi'),
      icon: <MailOutlineIcon />,
      color: '#ea4335',
      roles: ['admin', 'bayi', 'musteri', 'personel'],
      items: [
        { text: t('Mail Özeti'), icon: <DashboardIcon />, path: '/mail/dashboard', roles: ['admin', 'bayi', 'musteri', 'personel'], color: '#5e72e4' },
        { text: t('Domainler'), icon: <DomainIcon />, path: '/mail/domains', roles: ['admin', 'bayi', 'musteri', 'personel'], color: '#ea4335' },
        { text: t('Mail Hesapları'), icon: <AlternateEmailIcon />, path: '/mail/accounts', roles: ['admin', 'bayi', 'musteri', 'personel'], color: '#4285f4' },
        { text: t('Teslimat ve Güvenlik'), icon: <HistoryIcon />, path: '/mail/delivery', roles: ['admin', 'bayi', 'musteri', 'personel'], color: '#ff9800' },
        { text: t('Müşteri Paketleri'), icon: <PaidIcon />, path: '/mail/customer-packages', roles: ['admin', 'bayi', 'personel'], color: '#9c27b0' },
        { text: t('Mail Altyapısı'), icon: <SettingsIcon />, path: '/mail/infrastructure', roles: ['admin'], color: '#78909c' },
      ]
    },
    {
      id: 'admin',
      title: t('Yönetim'),
      icon: <SettingsIcon />,
      color: '#6c757d',
      roles: ['admin', 'bayi', 'personel'],
      items: [
        { text: t('Kullanıcılar'), icon: <GroupIcon />, path: '/users', roles: ['admin', 'bayi', 'personel'], color: '#172b4d' },
        { text: t('AI Asistan Yönetimi'), icon: <SmartToyIcon />, path: '/ai-assistant/management', roles: ['admin', 'bayi', 'personel'], color: '#00897b' },
        { text: t('Aktivite Logları'), icon: <HistoryIcon />, path: '/activity-logs', roles: ['admin', 'bayi'], color: '#9c27b0' },
        { text: t('Hata Raporları'), icon: <BugReportIcon />, path: '/bug-reports', roles: ['admin', 'bayi', 'personel', 'musteri'], color: '#f44336' },
        { text: t('Ayarlar'), icon: <SettingsIcon />, path: '/settings', roles: ['admin', 'bayi', 'personel'], color: '#6c757d' },
      ]
    },
  ];

  // Path'ten sayfa key'ine mapping
  const pathToPageKey: Record<string, string> = {
    '/dashboard': 'dashboard',
    '/messages': 'messages',
    '/wa-business/numbers': 'wa-business-numbers',
    '/wa-business/ai-assistant': 'wa-business-ai-assistant',
    '/social/inbox': 'social-inbox',
    '/social/connections': 'social-connections',
    '/social/ai-assistant': 'social-ai-assistant',
    '/social/comments': 'social-comments',
    '/templates': 'templates-hub',
    '/waha': 'waha',
    '/waha/chat': 'waha-chat',
    '/waha/contacts': 'waha-contacts-hub',
    '/waha/bulk': 'waha-bulk',
    '/waha/departments': 'waha-departments',
    '/waha/group-tracker': 'group-tracker',
    '/waha/automation': 'waha-automation',
    '/waha/ai-assistant': 'waha-ai-assistant',
    '/waha/support-tickets': 'waha-conversations',
    '/whatsapp-contacts': 'whatsapp-contacts',
    '/campaigns': 'campaigns',
    '/email-campaigns': 'email-campaigns',
    '/email-lists': 'email-lists',
    '/email-templates': 'email-campaigns',
    '/sms-campaigns': 'sms-campaigns',
    '/google-ads': 'google-ads',
    '/google-ads/campaigns': 'google-ads-campaigns',
    '/google-ads/ad-groups': 'google-ads-ad-groups',
    '/google-ads/keywords': 'google-ads-keywords',
    '/google-ads/ads': 'google-ads-ads',
    '/google-ads/conversions': 'google-ads-conversions',
    '/google-ads/extensions': 'google-ads-extensions',
    '/google-ads/search-terms': 'google-ads-search-terms',
    '/google-ads/reports': 'google-ads-reports',
    '/google-ads/dashboards': 'google-ads-dashboards',
    '/google-ads/anomalies': 'google-ads-anomalies',
    '/google-ads/alerts': 'google-ads-anomalies',
    '/google-ads/url-health': 'google-ads-url-health',
    '/google-ads/insights': 'google-ads-insights',
    '/google-ads/budget-pacing': 'google-ads-budget-pacing',
    '/google-ads/assistant': 'google-ads-assistant',
    '/google-ads/ai-reports': 'google-ads-ai-reports',
    '/google-ads/ai-analysis': 'google-ads-ai-analysis',
    '/analiz-leads': 'analiz-leads',
    '/media': 'media',
    '/customers': 'customers',
    '/requests': 'requests',
    '/analytics': 'analytics',
    '/users': 'users-hub',
    '/activity-logs': 'activity-logs',
    '/bug-reports': 'bug-reports',
    '/settings': 'settings',
    '/subscription-management': 'subscription-management',
    '/mail/dashboard': 'mail-dashboard',
    '/mail/domains': 'mail-domains',
    '/mail/accounts': 'mail-accounts',
    '/mail/delivery': 'mail-queue',
    '/mail/customer-packages': 'mail-allocations',
    '/mail/infrastructure': 'mail-bayi-quotas',
    '/ai-assistant/management': 'ai-assistant-management',
    '/mail/my-panel': 'mail-my-panel',
  };

  // Check which pages the user can access
  const userAllowedPages = (user as any)?.allowed_pages as string[] | null;

  const isPageAllowed = (path: string): boolean => {
    // If allowed_pages is null or undefined, all pages are accessible (including admin)
    if (userAllowedPages === null || userAllowedPages === undefined) return true;

    // If allowed_pages is empty array, no pages are accessible
    if (userAllowedPages.length === 0) return false;

    const pageKey = pathToPageKey[path];
    // If not in mapping, deny access (for security)
    if (!pageKey) return false;

    return hasPageKey(userAllowedPages, pageKey);
  };

  // Build flat menuItems from categories (backward compatibility)
  const menuItems: MenuItem[] = menuCategories.flatMap(cat => cat.items);

  // Header trail for the current page: category / parent / page (e.g. "Pazarlama / Google Ads / Kampanyalar")
  const currentTrail: string[] = (() => {
    for (const cat of menuCategories) {
      for (const item of cat.items) {
        if (item.subItems?.some(sub => sub.path === location.pathname)) {
          const sub = item.subItems.find(x => x.path === location.pathname)!;
          return [item.text, sub.text];
        }
        if (item.path === location.pathname) return [cat.title, item.text];
      }
    }
    // Pages reached from inside another page rather than the menu
    const offMenu: Record<string, string[]> = { '/google-ads/dashboards': ['Google Ads', t('Panoları yönet')] };
    return offMenu[location.pathname] || ['Maxulus CRM'];
  })();

  // A sub-menu entry may narrow the roles of its parent (e.g. AI Analiz is admin/bayi only)
  const subRoleAllowed = (sub: MenuItem) => !sub.roles || sub.roles.includes(user?.role || '');

  // Global search — flat list of all navigable pages
  const allSearchablePages = menuCategories.flatMap(cat =>
    cat.items.flatMap(item => {
      const pages: { text: string; path: string; category: string; icon: React.ReactNode }[] = [];
      if (item.path) pages.push({ text: item.text, path: item.path, category: cat.title, icon: item.icon });
      if (item.subItems) {
        item.subItems.filter(subRoleAllowed).forEach(sub => {
          pages.push({ text: sub.text, path: sub.path, category: cat.title, icon: sub.icon || item.icon });
        });
      }
      return pages;
    })
  ).filter(page => {
    // Role + permission check
    const catItem = menuItems.find(m => m.path === page.path);
    if (catItem && !catItem.roles.includes(user?.role || '')) return false;
    return isPageAllowed(page.path);
  });

  const searchResults = searchQuery.trim().length > 0
    ? allSearchablePages.filter(page =>
        page.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
        page.category.toLowerCase().includes(searchQuery.toLowerCase())
      ).slice(0, 8)
    : [];

  // Auto-open category of current page on mount
  useEffect(() => {
    const path = location.pathname;
    for (const cat of menuCategories) {
      const match = cat.items.some(item =>
        item.path === path || item.subItems?.some(sub => sub.path === path)
      );
      if (match) {
        setOpenCategories(prev => prev[cat.id] ? prev : { ...prev, [cat.id]: true });
        // Also open sub-menu if applicable
        const parentItem = cat.items.find(item => item.subItems?.some(sub => sub.path === path));
        if (parentItem) {
          setOpenMenus(prev => prev[parentItem.path] ? prev : { ...prev, [parentItem.path]: true });
        }
        break;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Users who should not see Google Ads pages
  const hideGoogleAdsFor: string[] = [];

  const filteredMenuItems = menuItems.filter(item => {
    // Hide Google Ads for specific users
    if (hideGoogleAdsFor.includes(user?.username || '') && item.text === 'Google Ads') {
      return false;
    }

    // Role check
    const hasRoleAccess = item.roles.includes(user?.role || '');
    if (!hasRoleAccess) return false;

    // Page permission check
    return isPageAllowed(item.path);
  });

  const drawer = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      background: mode === 'dark'
        ? 'linear-gradient(180deg, #1a1a1a 0%, #0d0d0d 100%)'
        : 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)',
    }}>
      {/* Logo Section */}
      <Box sx={{
        p: collapsed ? 2.5 : 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: collapsed ? 'center' : 'flex-start',
        minHeight: 80,
        transition: 'padding 0.3s ease',
      }}>
        {collapsed ? (
          <Box component="img" src="/maxulus-icon-192.png" alt={t('Maxulus CRM')} width={34} height={34}
            sx={{ display: 'block', width: 34, height: 34, flexShrink: 0 }} />
        ) : (
          <MaxulusBrand height={24} label="CRM" dark={mode === 'dark'} />
        )}
      </Box>

      <Divider sx={{ opacity: 0.1 }} />

      {/* Navigation Menu - Kategorize Edilmiş */}
      <Box sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', px: collapsed ? 0.5 : 1.5, py: 1, transition: 'padding 0.3s ease' }}>
        {menuCategories
          .filter(category => {
            // Does at least one item in the category match the user's role and page permission?
            return category.items.some(item => {
              const hasRoleAccess = item.roles?.includes(user?.role || '');

              // If subItems exist, check if at least one subItem is allowed
              if (item.subItems && item.subItems.length > 0) {
                const hasAnySubItemAccess = item.subItems.some(subItem => subRoleAllowed(subItem) && isPageAllowed(subItem.path));
                return hasRoleAccess && hasAnySubItemAccess;
              }

              const hasPageAccess = isPageAllowed(item.path);
              return hasRoleAccess && hasPageAccess;
            });
          })
          .map((category) => {
            // Filtered items in category
            const filteredCategoryItems = category.items.filter(item => {
              const hasRoleAccess = !item.roles || item.roles.includes(user?.role || '');

              // If subItems exist, show parent menu if at least one subItem is allowed
              if (item.subItems && item.subItems.length > 0) {
                const hasAnySubItemAccess = item.subItems.some(subItem => subRoleAllowed(subItem) && isPageAllowed(subItem.path));
                return hasRoleAccess && hasAnySubItemAccess;
              }

              const hasPageAccess = isPageAllowed(item.path);
              return hasRoleAccess && hasPageAccess;
            });

            if (filteredCategoryItems.length === 0) return null;

            return (
              <Box key={category.id} sx={{ mb: 2 }}>
                {/* Kategori Başlığı - Tıklanabilir */}
                {!collapsed && (
                  <ListItemButton
                    onClick={() => handleCategoryToggle(category.id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1.5,
                      py: 1,
                      mb: 0.5,
                      borderRadius: 1,
                      '&:hover': {
                        backgroundColor: L.hover,
                        '& .expand-icon': { opacity: 1 }
                      }
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        fontSize: '0.6875rem',
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        color: L.inkTertiary,
                        flex: 1,
                      }}
                    >
                      {category.title}
                    </Typography>
                    {openCategories[category.id] ? (
                      <ExpandLess
                        className="expand-icon"
                        sx={{
                          fontSize: 20,
                          color: L.inkMuted,
                          opacity: 0.7,
                          transition: `opacity 150ms ${EASE_OUT}`
                        }}
                      />
                    ) : (
                      <ExpandMore
                        className="expand-icon"
                        sx={{
                          fontSize: 20,
                          color: L.inkMuted,
                          opacity: 0.7,
                          transition: `opacity 150ms ${EASE_OUT}`
                        }}
                      />
                    )}
                  </ListItemButton>
                )}

                {/* Kategori İçindeki Menü Öğeleri */}
                {openCategories[category.id] && (
                <List disablePadding>
                  {filteredCategoryItems.map((item) => {
                    // Filter subItems by page permissions
                    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
                    const filteredSubItems = item.subItems?.filter(subItem => {
                      if (!subRoleAllowed(subItem)) return false;
                      if (!isPageAllowed(subItem.path)) return false;
                      if ((subItem as any).condition && !(subItem as any).condition(currentUser)) return false;
                      return true;
                    }) || [];
                    const hasSubItems = filteredSubItems.length > 0;
                    const isActive = hasSubItems
                      ? filteredSubItems.some(sub => location.pathname === sub.path)
                      : location.pathname === item.path;
                    const isExactActive = location.pathname === item.path;

                    // If menu only has subItems and none are allowed, don't show it
                    if (item.subItems && item.subItems.length > 0 && filteredSubItems.length === 0) {
                      return null;
                    }

                    const menuButton = (
                      <ListItemButton
                        onClick={() => {
                          if (hasSubItems && !collapsed) {
                            toggleMenu(item.path);
                          } else {
                            navigate(item.path);
                          }
                        }}
                        sx={{
                          borderRadius: 1,
                          height: 36,
                          mx: collapsed ? 'auto' : 0.5,
                          px: collapsed ? 1.5 : 1.25,
                          justifyContent: collapsed ? 'center' : 'flex-start',
                          position: 'relative',
                          overflow: 'hidden',
                          transition: `background-color 150ms ${EASE_OUT}`,
                          backgroundColor: isExactActive ? L.hover : 'transparent',
                          // Active: a thin accent rule at the left edge, the only colour in the sidebar
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: `translateY(-50%) scaleY(${isActive ? 1 : 0})`,
                            width: 2,
                            height: 18,
                            backgroundColor: L.accent,
                            borderRadius: '0 2px 2px 0',
                            transition: `transform 150ms ${EASE_OUT}`,
                          },
                          '&:hover': {
                            backgroundColor: L.hover,
                            '& .MuiListItemIcon-root': { color: L.ink },
                          },
                        }}
                      >
                            <ListItemIcon
                              sx={{
                                minWidth: collapsed ? 'auto' : 32,
                                color: isActive ? L.accent : L.inkTertiary,
                                transition: `color 150ms ${EASE_OUT}`,
                                '& .MuiSvgIcon-root': { fontSize: '1.15rem' },
                              }}
                            >
                              {item.path === '/waha/ai-assistant' && assistantQuestionCount > 0 ? (
                                <Badge
                                  badgeContent={assistantQuestionCount}
                                  color="warning"
                                  sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', height: 16, minWidth: 16, fontWeight: 700 } }}
                                >
                                  {item.icon}
                                </Badge>
                              ) : (item.path === '/messages' || item.path === '/social/inbox') && messageCount > 0 ? (
                                <Badge
                                  badgeContent={messageCount}
                                  color="error"
                                  sx={{
                                    '& .MuiBadge-badge': {
                                      fontSize: '0.6rem',
                                      height: 16,
                                      minWidth: 16,
                                      fontWeight: 700,
                                    },
                                  }}
                                >
                                  {item.icon}
                                </Badge>
                              ) : (
                                item.icon
                              )}
                            </ListItemIcon>
                            {!collapsed && (
                              <>
                                <ListItemText
                                  primary={item.text}
                                  sx={{
                                    '& .MuiListItemText-primary': {
                                      fontSize: '0.8125rem',
                                      fontWeight: isActive ? 600 : 500,
                                      color: isActive ? L.ink : L.inkSecondary,
                                    },
                                  }}
                                />
                                {hasSubItems && (
                                  <Box
                                    sx={{
                                      color: L.inkMuted,
                                      display: 'flex',
                                      transition: `transform 150ms ${EASE_OUT}`,
                                      transform: openMenus[item.path] ? 'rotate(180deg)' : 'rotate(0deg)',
                                    }}
                                  >
                                    <ExpandMore fontSize="small" />
                                  </Box>
                                )}
                              </>
                            )}
                      </ListItemButton>
                    );

                    return (
                      // The sub-menu sits inside its parent <li> so the markup stays ul > li > ul > li
                      <ListItem key={item.text} disablePadding sx={{ mb: 0.25, display: 'block' }}>
                          {collapsed ? (
                            <Tooltip title={item.text} placement="right" arrow>
                              {menuButton}
                            </Tooltip>
                          ) : (
                            menuButton
                          )}

                        {/* Sub-menu items */}
                        {hasSubItems && !collapsed && (
                          <Collapse in={openMenus[item.path]} timeout="auto" unmountOnExit>
                            <List disablePadding sx={{ pl: 1, mt: 0.25 }}>
                              {filteredSubItems.map((subItem, subIndex) => {
                                const groupLabel = subItem.group && subItem.group !== filteredSubItems[subIndex - 1]?.group ? subItem.group : null;
                                // A query (?tab=, ?view=) keeps the page highlighted unless a sub-item links that exact query
                                const fullPath = location.pathname + location.search;
                                const isSubActive = subItem.path.includes('?')
                                  ? fullPath === subItem.path
                                  : location.pathname === subItem.path && !filteredSubItems.some(s => s.path.includes('?') && s.path === fullPath);
                                return (
                                  <React.Fragment key={subItem.text}>
                                  {groupLabel && (
                                    <Typography component="li" sx={{ pl: 3.5, pr: 1, pt: subIndex ? 1.25 : 0.5, pb: 0.5, fontSize: '0.6875rem', fontWeight: 500, color: L.inkTertiary }}>
                                      {groupLabel}
                                    </Typography>
                                  )}
                                  <ListItem disablePadding sx={{ mb: 0.25 }}>
                                    <ListItemButton
                                      onClick={() => navigate(subItem.path)}
                                      sx={{
                                        borderRadius: 1,
                                        height: 32,
                                        mx: 0.5,
                                        pl: 3.5,
                                        position: 'relative',
                                        transition: `background-color 150ms ${EASE_OUT}`,
                                        backgroundColor: isSubActive ? L.hover : 'transparent',
                                        '&::before': {
                                          content: '""',
                                          position: 'absolute',
                                          left: 0,
                                          top: '50%',
                                          transform: `translateY(-50%) scaleY(${isSubActive ? 1 : 0})`,
                                          width: 2,
                                          height: 16,
                                          backgroundColor: L.accent,
                                          borderRadius: '0 2px 2px 0',
                                          transition: `transform 150ms ${EASE_OUT}`,
                                        },
                                        '&:hover': {
                                          backgroundColor: L.hover,
                                          '& .MuiListItemIcon-root': { color: L.ink },
                                        },
                                      }}
                                    >
                                      <ListItemIcon
                                        sx={{
                                          minWidth: 28,
                                          color: isSubActive ? L.accent : L.inkMuted,
                                          transition: `color 150ms ${EASE_OUT}`,
                                          '& .MuiSvgIcon-root': {
                                            fontSize: '1.1rem',
                                          },
                                        }}
                                      >
                                        {subItem.path === '/messages' && messageCount > 0 ? (
                                          <Badge badgeContent={messageCount} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.55rem', height: 14, minWidth: 14, fontWeight: 700 } }}>
                                            {subItem.icon}
                                          </Badge>
                                        ) : subItem.icon}
                                      </ListItemIcon>
                                      <ListItemText
                                        primary={subItem.text}
                                        sx={{
                                          '& .MuiListItemText-primary': {
                                            fontSize: '0.8125rem',
                                            fontWeight: isSubActive ? 600 : 400,
                                            color: isSubActive ? L.ink : L.inkSecondary,
                                          },
                                        }}
                                      />
                                    </ListItemButton>
                                  </ListItem>
                                  </React.Fragment>
                                );
                              })}
                            </List>
                          </Collapse>
                        )}
                      </ListItem>
                    );
                  })}
                </List>
                )}
              </Box>
            );
          })}
      </Box>

      {/* User Section */}
      {!collapsed && (
        <>
          <Divider sx={{ opacity: 0.1 }} />
          <Box sx={{ p: 1.5 }}>
            <Box
              sx={{
                p: 1.5,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                borderRadius: 1.5,
                border: `1px solid ${L.rule}`,
                backgroundColor: L.surface,
                cursor: 'pointer',
                transition: `background-color 150ms ${EASE_OUT}`,
                '&:hover': { backgroundColor: L.hover },
              }}
              onClick={() => navigate('/profile')}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: L.accentSoft,
                  color: L.accent,
                  fontWeight: 600,
                  fontSize: '0.8125rem',
                  flexShrink: 0,
                }}
              >
                {(user?.username || '?')[0].toUpperCase()}
              </Box>
              <Box sx={{ overflow: 'hidden', flex: 1 }}>
                <Typography variant="body2" fontWeight={600} noWrap sx={{ color: 'text.primary' }}>
                  {user?.company_name || user?.username}
                </Typography>
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    display: 'inline-block',
                    color: L.inkTertiary,
                    fontWeight: 500,
                    fontSize: '0.71875rem',
                    mt: 0.125,
                  }}
                >
                  {user?.role === 'admin' ? t('Yönetici') : user?.role === 'bayi' ? t('Bayi') : user?.role === 'personel' ? t('Personel') : user?.role === 'musteri' ? t('Müşteri') : user?.role}
                </Typography>
              </Box>
            </Box>
          </Box>
        </>
      )}

      {/* Collapse Toggle Button - En Altta (sadece desktop) */}
      <Divider sx={{ opacity: 0.1, display: { xs: 'none', sm: 'block' } }} />
      <Box sx={{
        p: collapsed ? 1.5 : 2,
        transition: 'padding 0.3s ease',
        display: { xs: 'none', sm: 'block' },
      }}>
        <Tooltip title={collapsed ? t('Menüyü Genişlet') : t('Menüyü Daralt')} placement="right" arrow>
          <Box
            onClick={toggleCollapse}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: collapsed ? 'center' : 'flex-start',
              gap: 1.5,
              p: collapsed ? 1.25 : 1.5,
              borderRadius: 1,
              cursor: 'pointer',
              transition: `background-color 150ms ${EASE_OUT}`,
              '&:hover': {
                backgroundColor: L.hover,
                '& .collapse-text': { color: L.ink },
              },
            }}
          >
            <Box
              className="collapse-icon"
              sx={{
                width: 24,
                height: 24,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {collapsed ? (
                <ChevronRightIcon sx={{ fontSize: 18, color: L.inkTertiary }} />
              ) : (
                <ChevronLeftIcon sx={{ fontSize: 18, color: L.inkTertiary }} />
              )}
            </Box>
            {!collapsed && (
              <Typography
                className="collapse-text"
                variant="body2"
                sx={{
                  fontWeight: 500,
                  color: L.inkTertiary,
                  fontSize: '0.8125rem',
                  transition: `color 150ms ${EASE_OUT}`,
                }}
              >
                {t('Menüyü Daralt')}
              </Typography>
            )}
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <ThemeProvider theme={densityTheme}>
    {/* Compact: the rem base drops ~6% (a percentage keeps the reader's browser font size in play) */}
    <GlobalStyles styles={compact ? { html: { fontSize: '93.75%' } } : {}} />
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: L.canvas }}>
      {/* Modern AppBar - Premium Design */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH}px)` },
          ml: { sm: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH },
          backgroundColor: L.canvas,
          color: L.ink,
          border: 'none',
          borderBottom: `1px solid ${L.rule}`,
          borderRadius: 0,
          boxShadow: 'none',
        }}
      >
        <Toolbar sx={{ height: HEADER_HEIGHT, minHeight: `${HEADER_HEIGHT}px !important`, px: { xs: 2, sm: 3 }, gap: 2 }}>
          {/* Mobile Menu Button */}
          <IconButton
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              display: { sm: 'none' },
              color: L.ink,
              border: `1px solid ${L.rule}`,
            }}
          >
            <MenuIcon />
          </IconButton>

          {/* Breadcrumb: where you are. The page itself carries the large title. */}
          <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 0.75, overflow: 'hidden' }}>
            {currentTrail.slice(0, -1).map(seg => (
              <React.Fragment key={seg}>
                <Typography noWrap sx={{ fontSize: '0.8125rem', color: L.inkTertiary, display: { xs: 'none', xl: 'block' }, flexShrink: 0 }}>{seg}</Typography>
                <Typography sx={{ fontSize: '0.8125rem', color: L.inkMuted, display: { xs: 'none', xl: 'block' }, flexShrink: 0 }}>/</Typography>
              </React.Fragment>
            ))}
            <Typography noWrap sx={{ fontSize: '0.875rem', fontWeight: 600, color: L.ink, flexShrink: 0 }}>
              {currentTrail[currentTrail.length - 1]}
            </Typography>
          </Box>

          {/* Right Actions */}
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Session Selector - visible on WAHA pages, BEFORE search bar */}
            {showSessionSelector && (() => {
              const formatPhone = (num: string) => {
                const d = num.replace(/\D/g, '');
                if (d.length === 12 && d.startsWith('90')) return `+${d.slice(0,2)} ${d.slice(2,5)} ${d.slice(5,8)} ${d.slice(8)}`;
                if (d.length > 6) return `+${d.slice(0,2)} ${d.slice(2)}`;
                return `+${d}`;
              };
              const getSessionLabel = (s: any) => {
                const name = s.phoneName || s.owner?.company_name || s.owner?.username || '';
                const phone = s.phoneNumber ? formatPhone(s.phoneNumber) : '';
                if (name && phone) return { name, phone };
                if (phone) return { name: phone, phone: '' };
                return { name: name || s.sessionName.substring(0, 15), phone: '' };
              };
              const statusColor = (st: string) => st === 'WORKING' ? '#4caf50' : st === 'SCAN_QR_CODE' ? '#ff9800' : st === 'STARTING' ? '#2196f3' : '#bdbdbd';
              const statusLabel = (st: string) => st === 'WORKING' ? t('Bağlı') : st === 'SCAN_QR_CODE' ? t('QR Bekliyor') : st === 'STARTING' ? t('Başlatılıyor') : t('Durduruldu');
              return (
              <FormControl size="small" sx={{ display: { xs: 'none', md: 'flex' }, minWidth: 200 }}>
                <Select
                  value={activeSession || ''}
                  onChange={(e) => {
                    const val = e.target.value as string;
                    if (val === '__new__') return;
                    setActiveSession(val);
                  }}
                  displayEmpty
                  sx={{
                    height: 36,
                    borderRadius: 2,
                    fontSize: '0.82rem',
                    fontWeight: 500,
                    '& .MuiSelect-select': { display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5 },
                  }}
                  renderValue={(value) => {
                    const s = sessions.find(s => s.sessionName === value);
                    if (!s) return t('Oturum yok');
                    const { name, phone } = getSessionLabel(s);
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: statusColor(s.status), flexShrink: 0 }} />
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {name}{phone ? ` \u00B7 ${phone}` : ''}
                        </Typography>
                      </Box>
                    );
                  }}
                >
                  {sessions.map(s => {
                    const { name, phone } = getSessionLabel(s);
                    return (
                      <MenuItem key={s.sessionName} value={s.sessionName}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: statusColor(s.status), flexShrink: 0, mt: 0.5 }} />
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{name}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {phone ? `${phone} \u00B7 ` : ''}{statusLabel(s.status)}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    );
                  })}
                  {canCreateSession && <Divider />}
                  {canCreateSession && (
                    <MenuItem
                      value="__new__"
                      onClick={(e) => { e.preventDefault(); createNewSession().catch(() => {}); }}
                      sx={{ color: '#1976D2', fontWeight: 600 }}
                    >
                      {t('+ Yeni Oturum')}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
              );
            })()}

            {/* Google Ads Account Selector + Date Picker */}
            {isGoogleAdsPage && <GoogleAdsAccountSelector />}
            {isGoogleAdsPage && <GoogleAdsDatePicker />}

            {/* Search Bar - Modern Design */}
            <Box sx={{ position: 'relative', display: { xs: 'none', lg: 'block' } }}>
              <SearchBar
                elevation={0}
                sx={{
                  // Narrow when the Ads account/date controls share the bar, so the breadcrumb stays readable
                  width: isGoogleAdsPage ? 200 : 260,
                  height: 34,
                  borderRadius: 1,
                  px: 1.25,
                  backgroundColor: L.inset,
                  border: `1px solid ${searchFocused ? L.accent : L.ruleStrong}`,
                  boxShadow: searchFocused ? `0 0 0 3px ${L.accentSoft}` : 'none',
                  transition: `border-color 150ms ${EASE_OUT}, box-shadow 150ms ${EASE_OUT}`,
                  '&:hover': { backgroundColor: L.inset, boxShadow: searchFocused ? `0 0 0 3px ${L.accentSoft}` : 'none' },
                  '&:focus-within': { maxWidth: 'none', border: `1px solid ${L.accent}` },
                }}
              >
                <SearchIcon sx={{ color: L.inkMuted, mr: 1, fontSize: 17 }} />
                <InputBase
                  placeholder={t('Sayfa ara...')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onFocus={() => setSearchFocused(true)}
                  onBlur={() => setTimeout(() => setSearchFocused(false), 200)}
                  sx={{
                    flex: 1,
                    fontSize: '0.8125rem',
                    color: 'text.primary',
                    '& input': {
                      padding: 0,
                      '&::placeholder': {
                        color: mode === 'dark' ? alpha('#fff', 0.4) : alpha('#000', 0.4),
                        opacity: 1,
                      }
                    }
                  }}
                />
              </SearchBar>
              {/* Search Results Dropdown */}
              {searchFocused && searchResults.length > 0 && (
                <Paper
                  elevation={8}
                  sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    mt: 0.5,
                    borderRadius: 2,
                    overflow: 'hidden',
                    zIndex: 9999,
                    maxHeight: 360,
                    overflowY: 'auto',
                  }}
                >
                  {searchResults.map((page, i) => (
                    <Box
                      key={page.path}
                      onClick={() => { navigate(page.path); setSearchQuery(''); setSearchFocused(false); }}
                      sx={{
                        px: 2, py: 1.25,
                        display: 'flex', alignItems: 'center', gap: 1.5,
                        cursor: 'pointer',
                        borderBottom: i < searchResults.length - 1 ? `1px solid ${alpha(theme.palette.divider, 0.08)}` : 'none',
                        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
                      }}
                    >
                      <Box sx={{ color: theme.palette.primary.main, display: 'flex', '& .MuiSvgIcon-root': { fontSize: 18 } }}>
                        {page.icon}
                      </Box>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{page.text}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>{page.category}</Typography>
                      </Box>
                    </Box>
                  ))}
                </Paper>
              )}
              {searchFocused && searchQuery.trim().length > 0 && searchResults.length === 0 && (
                <Paper
                  elevation={8}
                  sx={{ position: 'absolute', top: '100%', left: 0, right: 0, mt: 0.5, borderRadius: 2, p: 2, zIndex: 9999, textAlign: 'center' }}
                >
                  <Typography variant="body2" color="text.secondary">{t('Sonuç bulunamadı')}</Typography>
                </Paper>
              )}
            </Box>

            {/* Action Buttons Container */}
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                backgroundColor: mode === 'dark'
                  ? alpha('#ffffff', 0.04)
                  : alpha('#000000', 0.03),
                borderRadius: 3,
                p: 0.5,
              }}
            >
              {/* Notifications */}
              <NotificationMenu />

              {/* Mentions - only on WAHA pages */}
              {isWahaPage && (
                <Tooltip title={t('Etiketlenmelerim')} arrow>
                  <IconButton onClick={handleMentionDrawerOpen} sx={{ width: 38, height: 38, borderRadius: 2, color: '#ff9800', '&:hover': { bgcolor: alpha('#ff9800', 0.1) } }}>
                    <Badge badgeContent={mentionCount} color="warning" sx={{ '& .MuiBadge-badge': { fontSize: '0.6rem', minWidth: 16, height: 16 } }}>
                      <AlternateEmailIcon sx={{ fontSize: 20 }} />
                    </Badge>
                  </IconButton>
                </Tooltip>
              )}

              <BugReportButton />

              {/* Language: globe + current code in the header, both languages named in their own language */}
              {canChoose && (
                <Tooltip title={t('Dil')} arrow>
                  <Button
                    onClick={(e) => setLanguageAnchor(e.currentTarget)}
                    aria-haspopup="menu"
                    aria-expanded={Boolean(languageAnchor)}
                    aria-controls={languageAnchor ? 'language-menu' : undefined}
                    aria-label={`${t('Dil')}: ${language.toUpperCase()}`}
                    startIcon={<LanguageIcon sx={{ fontSize: '18px !important' }} />}
                    endIcon={<ArrowDownIcon sx={{ fontSize: '16px !important', transition: 'transform 150ms ease-out', transform: languageAnchor ? 'rotate(180deg)' : 'none' }} />}
                    sx={{
                      height: 38,
                      px: { xs: 0, sm: 1.25 },
                      minWidth: { xs: 38, sm: 0 },
                      borderRadius: 2,
                      color: L.inkSecondary,
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      letterSpacing: '0.04em',
                      fontVariantNumeric: 'tabular-nums',
                      // Phones keep the compact code only, so the header still fits at 360px
                      '& .MuiButton-startIcon': { mr: 0.75, ml: 0, display: { xs: 'none', sm: 'inherit' } },
                      '& .MuiButton-endIcon': { ml: 0.25, mr: 0, display: { xs: 'none', sm: 'inherit' } },
                      transition: 'background-color 150ms ease-out, color 150ms ease-out',
                      '&:hover': { bgcolor: alpha(L.ink, 0.08), color: L.ink },
                    }}
                  >
                    {language.toUpperCase()}
                  </Button>
                </Tooltip>
              )}

              {/* Theme Toggle */}
              <Tooltip title={mode === 'dark' ? t('Aydınlık Mod') : t('Karanlık Mod')} arrow>
                <IconButton
                  onClick={toggleColorMode}
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 2,
                    color: mode === 'dark' ? '#ffd93d' : '#6c757d',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: mode === 'dark'
                        ? alpha('#ffd93d', 0.15)
                        : alpha('#000000', 0.08),
                      transform: 'rotate(180deg)',
                    }
                  }}
                >
                  {mode === 'dark' ? <LightModeIcon sx={{ fontSize: 20 }} /> : <DarkModeIcon sx={{ fontSize: 20 }} />}
                </IconButton>
              </Tooltip>
            </Stack>

            {/* Profile Button - Premium Style */}
            <Button
              onClick={handleProfileMenuOpen}
              sx={{
                borderRadius: 3,
                // Same height as the icon group on its left (38px buttons + 4px padding)
                height: 46,
                py: 0,
                px: { xs: 1, sm: 1.5 },
                ml: 0.5,
                backgroundColor: mode === 'dark'
                  ? alpha('#ffffff', 0.04)
                  : alpha('#000000', 0.03),
                border: `1px solid ${mode === 'dark'
                  ? alpha('#ffffff', 0.08)
                  : alpha('#000000', 0.06)}`,
                textTransform: 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: mode === 'dark'
                    ? alpha('#ffffff', 0.08)
                    : alpha('#000000', 0.06),
                  borderColor: mode === 'dark'
                    ? alpha('#ffffff', 0.15)
                    : alpha('#000000', 0.12),
                  transform: 'translateY(-1px)',
                  boxShadow: mode === 'dark'
                    ? '0 4px 12px rgba(0,0,0,0.3)'
                    : '0 4px 12px rgba(0,0,0,0.08)',
                }
              }}
            >
              <Box sx={{ position: 'relative' }}>
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    fontSize: '0.8125rem',
                  }}
                >
                  {user?.username?.[0]?.toUpperCase()}
                </Avatar>
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: '#2dce89',
                    border: `2px solid ${mode === 'dark' ? '#141414' : '#ffffff'}`,
                  }}
                />
              </Box>
              <Box sx={{ textAlign: 'left', ml: 1.5, display: { xs: 'none', md: 'block' } }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: '0.85rem',
                    lineHeight: 1.2,
                  }}
                >
                  {user?.username}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    color: L.inkTertiary,
                    fontSize: '0.7rem',
                    textTransform: 'capitalize',
                  }}
                >
                  {user?.role === 'admin' ? t('Yönetici') : user?.role === 'bayi' ? t('Bayi') : user?.role === 'personel' ? t('Personel') : user?.role === 'musteri' ? t('Müşteri') : t('Kullanıcı')}
                </Typography>
              </Box>
              <KeyboardArrowDown
                sx={{
                  ml: { xs: 0, md: 0.5 },
                  fontSize: 18,
                  color: mode === 'dark' ? alpha('#fff', 0.4) : alpha('#000', 0.4),
                  transition: 'transform 0.2s',
                  '.MuiButton-root:hover &': {
                    transform: 'rotate(180deg)',
                  }
                }}
              />
            </Button>
          </Stack>
        </Toolbar>
      </AppBar>

      {/* Sidebar */}
      <Box
        component="nav"
        sx={{ 
          width: { sm: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH }, 
          flexShrink: { sm: 0 },
          transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        <StyledDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: DRAWER_WIDTH,
            },
          }}
        >
          {drawer}
        </StyledDrawer>
        <StyledDrawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
              transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            },
          }}
          open
        >
          {drawer}
        </StyledDrawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: compact ? 2.5 : 3 },
          width: { sm: `calc(100% - ${collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH}px)` },
          mt: `${HEADER_HEIGHT + 12}px`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          minHeight: `calc(100vh - ${HEADER_HEIGHT + 12}px)`,
          overflow: 'hidden',
          backgroundColor: mode === 'dark' ? '#0a0a0a' : '#f8fafc',
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: '100%',
            overflow: 'auto',
            position: 'relative',
          }}
        >
          <Suspense
            fallback={
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  width: '100%',
                }}
              >
                <Typography>{t('Yükleniyor...')}</Typography>
              </Box>
            }
          >
            <Outlet />
          </Suspense>
        </Box>
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0 8px 32px rgba(0,0,0,0.08))',
            mt: 1.5,
            borderRadius: 2,
            minWidth: 220,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {user?.company_name || user?.username}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {user?.email}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => { navigate('/profile'); handleProfileMenuClose(); }}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          {t('Profil')}
        </MenuItem>
        {user?.role !== 'musteri' && (
          <MenuItem onClick={() => { navigate('/settings'); handleProfileMenuClose(); }}>
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            {t('Ayarlar')}
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleLogout} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          {t('Çıkış Yap')}
        </MenuItem>
      </Menu>

      {/* Language menu */}
      <Menu
        id="language-menu"
        anchorEl={languageAnchor}
        open={Boolean(languageAnchor)}
        onClose={() => setLanguageAnchor(null)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { elevation: 8, sx: { mt: 1, minWidth: 180, borderRadius: 2 } } }}
      >
        {languages.map(({ code, nativeName: name }) => (
          <MenuItem
            key={code}
            selected={language === code}
            role="menuitemradio"
            aria-checked={language === code}
            onClick={() => { setLanguageAnchor(null); setLanguage(code); }}
            sx={{ gap: 1.5 }}
          >
            <Typography component="span" sx={{ width: 22, fontSize: '0.75rem', fontWeight: 600, color: L.inkTertiary, letterSpacing: '0.04em' }}>
              {code.toUpperCase()}
            </Typography>
            <Typography component="span" sx={{ flex: 1, fontSize: '0.875rem' }}>{name}</Typography>
            {language === code && <CheckIcon sx={{ fontSize: 18, color: L.accent }} />}
          </MenuItem>
        ))}
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationAnchor}
        open={Boolean(notificationAnchor)}
        onClose={handleNotificationClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0 8px 32px rgba(0,0,0,0.08))',
            mt: 1.5,
            borderRadius: 2,
            minWidth: 360,
            maxHeight: 480,
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <Box sx={{ 
          px: 2, 
          py: 1.5, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: mode === 'dark' ? 'background.default' : 'grey.50'
        }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <NotificationsIcon fontSize="small" color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>
              {t('Bildirimler')}
            </Typography>
            {notificationCount > 0 && (
              <Chip 
                label={notificationCount}
                size="small"
                color="error"
                sx={{ height: 20, minWidth: 20 }}
              />
            )}
          </Stack>
          <Stack direction="row" spacing={0.5}>
            {notifications.length > 0 && (
              <>
                <Tooltip title={t('Tümünü okundu işaretle')}>
                  <IconButton size="small" onClick={handleMarkAllRead}>
                    <DoneAllIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('Tümünü temizle')}>
                  <IconButton size="small" onClick={handleClearAll}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <IconButton size="small" onClick={handleNotificationClose}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>
        <Divider />
        
        {notifications.length > 0 ? (
          <List sx={{ maxHeight: 400, overflow: 'auto', p: 0 }}>
            {notifications.map((notification) => (
              <ListItem
                key={notification.id}
                component="div"
                onClick={() => handleNotificationItemClick(notification)}
                sx={{
                  cursor: 'pointer',
                  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                  backgroundColor: notification.read 
                    ? 'transparent' 
                    : alpha(theme.palette.primary.main, 0.05),
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  }
                }}
              >
                <ListItemIcon>
                  {notification.type === 'message' ? (
                    <WhatsAppIcon sx={{ color: '#1976D2' }} />
                  ) : notification.type === 'campaign' ? (
                    <CampaignIcon color="primary" />
                  ) : (
                    <NotificationsIcon color="action" />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" fontWeight={notification.read ? 400 : 600}>
                      {notification.title}
                    </Typography>
                  }
                  secondary={
                    <Stack spacing={0.5}>
                      <Typography variant="body2" color="text.secondary" sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {notification.message}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(notification.timestamp).toLocaleString(getLocale())}
                      </Typography>
                    </Stack>
                  }
                />
                {!notification.read && (
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    backgroundColor: 'error.main',
                    ml: 1 
                  }} />
                )}
              </ListItem>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsNoneOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {t('Yeni bildirim yok')}
            </Typography>
          </Box>
        )}
      </Menu>

      {/* Mentions Drawer */}
      <Drawer anchor="right" open={mentionDrawerOpen} onClose={() => setMentionDrawerOpen(false)} PaperProps={{ sx: { width: 380, borderRadius: '12px 0 0 12px', bgcolor: 'background.paper' } }}>
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={700} color="text.primary">{t('Etiketlenmelerim')}</Typography>
            <Stack direction="row" spacing={0.5}>
              {mentionCount > 0 && (
                <Tooltip title={t('Tümünü okundu işaretle')}>
                  <IconButton size="small" sx={{ color: 'text.secondary' }} onClick={async () => {
                    await api.put('/waha/mentions/read-all').catch(() => {});
                    setMentionCount(0);
                    setRecentMentions(prev => prev.map((m: any) => ({ ...m, is_read: true })));
                  }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton size="small" sx={{ color: 'text.secondary' }} onClick={() => setMentionDrawerOpen(false)}>
                <CloseIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Stack>
          </Box>

          {mentionsLoading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          ) : recentMentions.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              {t('Henüz etiketlenmeniz yok')}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {recentMentions.map((m: any) => (
                <Paper
                  key={m.id}
                  elevation={0}
                  sx={{
                    p: 1.5, borderRadius: 2, cursor: 'pointer', transition: 'all 0.25s ease',
                    bgcolor: m.is_read ? alpha(theme.palette.background.default, 0.5) : alpha('#ff9800', 0.06),
                    border: `1px solid ${alpha(m.is_read ? theme.palette.divider : '#ff9800', 0.4)}`,
                    '&:hover': {
                      bgcolor: alpha('#1976D2', 0.08),
                      borderColor: alpha('#1976D2', 0.4),
                      transform: 'translateX(6px)',
                      boxShadow: `0 2px 8px ${alpha('#1976D2', 0.12)}`,
                      '& .mention-msg': { color: 'text.primary' }
                    }
                  }}
                  onClick={() => {
                    setMentionDrawerOpen(false);
                    const msgParam = m.message?.id ? `?tab=team&msg=note-${m.message.id}` : '?tab=team';
                    navigate(`/waha/support-tickets/${m.ticket_id}${msgParam}`);
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                    <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem', bgcolor: '#1976D2', color: '#fff' }}>
                      {(m.mentioner?.username || '?')[0].toUpperCase()}
                    </Avatar>
                    <Typography variant="caption" fontWeight={700} color="text.primary">{m.mentioner?.username || t('Bilinmeyen')}</Typography>
                    {!m.is_read && <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#ff9800' }} />}
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto !important', fontSize: '0.68rem' }}>
                      {m.created_at ? formatDistanceToNow(new Date(m.created_at), { addSuffix: true, locale: getDateFnsLocale() }) : ''}
                    </Typography>
                  </Stack>
                  {m.ticket && (
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.3, pl: 4, color: '#1976D2', fontWeight: 500 }}>
                      {m.ticket.chat_name || m.ticket.chat_phone || ''}
                    </Typography>
                  )}
                  <Typography className="mention-msg" variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem', pl: 4, transition: 'color 0.2s' }} noWrap>
                    {m.message?.content || ''}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          )}

          <Button
            fullWidth
            onClick={() => { setMentionDrawerOpen(false); navigate('/waha/support-tickets?tab=mentions'); }}
            sx={{ mt: 2, textTransform: 'none', fontWeight: 600, color: '#1976D2' }}
          >
            {t('Tümünü Gör')}
          </Button>
        </Box>
      </Drawer>
    </Box>
    </ThemeProvider>
  );
};

export default ModernLayout;